/**
 * Minimal tracer: one instance per request (Worker) or per page (browser).
 *
 * There is deliberately no global tracer provider and no async context
 * manager. Workers have no AsyncLocalStorage guarantee across all our entry
 * points, and an implicit ambient context is the main source of "span ended on
 * the wrong parent" bugs. Parenting here is explicit: you pass the parent span.
 */

import { attrs } from './attributes';
import { msToNanoString, newSpanId, newTraceId } from './ids';
import type { SpanContext } from './traceparent';
import {
	SeverityNumber,
	SpanKind,
	SpanStatusCode,
	type Attributes,
	type OtlpEvent,
	type OtlpKeyValue,
	type OtlpLink,
	type OtlpSpan,
	type OtlpTracePayload,
} from './types';

export type SpanOptions = {
	kind?: SpanKind;
	attributes?: Attributes;
	/** Explicit parent. Omit for a root span. */
	parent?: Span | SpanContext;
	links?: OtlpLink[];
	/** Override start time (epoch ms). Defaults to now. */
	startTimeMs?: number;
};

/**
 * Hard cap on spans held per tracer. Protects the single-node ClickHouse from
 * a pathological request (e.g. a bulk generation fanning out per record) and
 * bounds Worker memory. Overflow is counted and surfaced as an attribute on
 * the root span rather than silently dropped.
 */
const MAX_SPANS_PER_TRACE = 256;

/** Events per span. A runaway loop logging per iteration must not unbound. */
const MAX_EVENTS_PER_SPAN = 64;

export class Span {
	readonly traceId: string;
	readonly spanId: string;
	readonly parentSpanId?: string;
	readonly sampled: boolean;
	readonly traceState?: string;

	private readonly tracer: Tracer;
	private name: string;
	private readonly kind: SpanKind;
	private readonly startTimeMs: number;
	private readonly links?: OtlpLink[];
	private readonly attributes: Attributes = {};
	private readonly events: OtlpEvent[] = [];
	private droppedEvents = 0;
	private status: { code: SpanStatusCode; message?: string } = { code: SpanStatusCode.UNSET };
	private endTimeMs?: number;
	private ended = false;

	constructor(tracer: Tracer, name: string, options: SpanOptions = {}) {
		this.tracer = tracer;
		this.name = name;
		this.kind = options.kind ?? SpanKind.INTERNAL;
		this.startTimeMs = options.startTimeMs ?? Date.now();
		this.links = options.links;

		const parent = options.parent;
		const parentCtx: SpanContext | undefined =
			parent instanceof Span
				? { traceId: parent.traceId, spanId: parent.spanId, sampled: parent.sampled, traceState: parent.traceState }
				: parent;

		this.traceId = parentCtx?.traceId ?? tracer.traceId;
		this.parentSpanId = parentCtx?.spanId;
		this.spanId = newSpanId();
		// An in-process parent's flag is authoritative; a REMOTE one is not. The
		// tracer's decision already accounts for the inbound context (see
		// startServerTrace), so letting a raw remote `sampled=1` win here would
		// re-admit the same bypass at span level and propagate sampled=1
		// downstream on a trace this Worker decided to drop.
		this.sampled = parent instanceof Span ? parent.sampled : tracer.sampled;
		this.traceState = parentCtx?.traceState ?? tracer.traceState;

		if (options.attributes) Object.assign(this.attributes, options.attributes);
	}

	/** Span context suitable for propagation or for parenting a child span. */
	get context(): SpanContext {
		return { traceId: this.traceId, spanId: this.spanId, sampled: this.sampled, traceState: this.traceState };
	}

	/**
	 * Rename the span after creation.
	 *
	 * Needed because the low-cardinality route pattern is only known once the
	 * router has matched, which is after the span has to exist.
	 */
	updateName(name: string): this {
		this.name = name;
		return this;
	}

	setAttribute(key: string, value: Attributes[string]): this {
		this.attributes[key] = value;
		return this;
	}

	setAttributes(values: Attributes): this {
		Object.assign(this.attributes, values);
		return this;
	}

	addEvent(name: string, attributes?: Attributes, timeMs?: number): this {
		if (this.events.length >= MAX_EVENTS_PER_SPAN) {
			this.droppedEvents++;
			return this;
		}
		this.events.push({
			timeUnixNano: msToNanoString(timeMs ?? Date.now()),
			name,
			attributes: attrs(attributes),
		});
		return this;
	}

	/**
	 * Structured log record carried as a span event.
	 *
	 * The collector has no logs pipeline (traces only, by design — see
	 * otel/README.md), so log-like records are emitted as span events. They
	 * land in otel.otel_traces.Events.* and stay correlated with the span,
	 * which is what makes them useful. Severity follows the OTel logs data
	 * model so the same records can move to a real logs pipeline unchanged if
	 * one is ever added.
	 */
	log(severity: SeverityNumber, message: string, attributes?: Attributes): this {
		return this.addEvent('log', {
			...attributes,
			'log.severity_number': severity,
			'log.severity_text': severityText(severity),
			'log.message': message,
		});
	}

	debug(message: string, attributes?: Attributes): this {
		return this.log(SeverityNumber.DEBUG, message, attributes);
	}

	info(message: string, attributes?: Attributes): this {
		return this.log(SeverityNumber.INFO, message, attributes);
	}

	warn(message: string, attributes?: Attributes): this {
		return this.log(SeverityNumber.WARN, message, attributes);
	}

	error(message: string, attributes?: Attributes): this {
		return this.log(SeverityNumber.ERROR, message, attributes);
	}

	setStatus(code: SpanStatusCode, message?: string): this {
		this.status = { code, message };
		return this;
	}

	/**
	 * Record an exception as the conventional `exception` event and mark the
	 * span errored. Stack traces are included — they are the whole point of
	 * having the event — but truncated by the attribute normaliser.
	 */
	recordException(error: unknown, attributes?: Attributes): this {
		const err = error instanceof Error ? error : undefined;
		this.addEvent('exception', {
			...attributes,
			'exception.type': err?.name ?? typeof error,
			'exception.message': err?.message ?? String(error),
			'exception.stacktrace': err?.stack,
		});
		return this.setStatus(SpanStatusCode.ERROR, err?.message ?? String(error));
	}

	/** Idempotent: a double end() keeps the first end time. */
	end(endTimeMs?: number): void {
		if (this.ended) return;
		this.ended = true;
		const end = endTimeMs ?? Date.now();
		// Never allow end < start. Duration is a UInt64 in the ClickHouse
		// schema, so a negative value does not surface as negative — it wraps
		// to ~1.8e19 ns and silently poisons every latency aggregate over the
		// table. Clamping to a zero-length span is strictly better than that.
		this.endTimeMs = end < this.startTimeMs ? this.startTimeMs : end;
		this.tracer._record(this);
	}

	get isEnded(): boolean {
		return this.ended;
	}

	/** @internal */
	_toOtlp(): OtlpSpan {
		const attributes = attrs(
			this.droppedEvents > 0
				? { ...this.attributes, 'otel.dropped_events_count': this.droppedEvents }
				: this.attributes,
		);
		return {
			traceId: this.traceId,
			spanId: this.spanId,
			...(this.parentSpanId ? { parentSpanId: this.parentSpanId } : {}),
			name: this.name,
			kind: this.kind,
			startTimeUnixNano: msToNanoString(this.startTimeMs),
			endTimeUnixNano: msToNanoString(this.endTimeMs ?? this.startTimeMs),
			...(attributes ? { attributes } : {}),
			...(this.events.length > 0 ? { events: this.events } : {}),
			...(this.links && this.links.length > 0 ? { links: this.links } : {}),
			status: this.status,
		};
	}
}

function severityText(severity: SeverityNumber): string {
	switch (severity) {
		case SeverityNumber.TRACE:
			return 'TRACE';
		case SeverityNumber.DEBUG:
			return 'DEBUG';
		case SeverityNumber.INFO:
			return 'INFO';
		case SeverityNumber.WARN:
			return 'WARN';
		case SeverityNumber.ERROR:
			return 'ERROR';
		case SeverityNumber.FATAL:
			return 'FATAL';
		default:
			return 'INFO';
	}
}

export type TracerOptions = {
	/** Resource-level attributes, e.g. service.name, deployment.environment. */
	resource: Attributes;
	/** Instrumentation scope name, and optional scope attributes. */
	scopeName?: string;
	scopeVersion?: string;
	scopeAttributes?: Attributes;
	/** Inbound trace context, if the caller propagated one. */
	parentContext?: SpanContext | null;
	/** Head sampling decision for this trace. */
	sampled?: boolean;
	/**
	 * Reuse an already-generated trace id instead of minting a new one.
	 *
	 * Needed because the sampling decision is derived from the trace id, so a
	 * caller that has already decided must be able to keep the id it decided on.
	 * Regenerating would propagate an id downstream that hashes to a different
	 * verdict, breaking the determinism the whole scheme relies on.
	 */
	traceId?: string;
};

export class Tracer {
	readonly traceId: string;
	readonly sampled: boolean;
	readonly traceState?: string;
	readonly rootParentContext?: SpanContext;

	private readonly resource: Attributes;
	private readonly scopeName: string;
	private readonly scopeVersion?: string;
	private readonly scopeAttributes?: Attributes;
	private readonly finished: Span[] = [];
	private droppedSpans = 0;

	constructor(options: TracerOptions) {
		const parent = options.parentContext ?? undefined;
		this.resource = options.resource;
		this.scopeName = options.scopeName ?? 'automadoc';
		this.scopeVersion = options.scopeVersion;
		this.scopeAttributes = options.scopeAttributes;
		this.rootParentContext = parent;
		this.traceId = parent?.traceId ?? options.traceId ?? newTraceId();
		this.traceState = parent?.traceState;
		// Precedence matters: an EXPLICIT `sampled` wins over the parent's flag.
		// The reverse — parent always wins — silently defeated the caller's
		// decision, and since the tracing middleware runs before auth on a public
		// Worker, that let any caller force `sampled=1` via a forged `traceparent`
		// and bypass OTEL_SAMPLE_RATE entirely. An upstream flag is still
		// inherited when the caller expresses no preference, so a genuinely
		// sampled distributed trace is not left with a hole in the middle; see
		// `trustRemoteSampling` in http.ts for where that trust is granted.
		this.sampled = options.sampled ?? parent?.sampled ?? true;
	}

	startSpan(name: string, options: SpanOptions = {}): Span {
		// Root spans inherit the inbound remote context so the Worker span
		// attaches to the caller's trace rather than starting a new one.
		if (!options.parent && this.rootParentContext) {
			options = { ...options, parent: this.rootParentContext };
		}
		return new Span(this, name, options);
	}

	/** @internal — called by Span.end(). */
	_record(span: Span): void {
		if (this.finished.length >= MAX_SPANS_PER_TRACE) {
			this.droppedSpans++;
			return;
		}
		this.finished.push(span);
	}

	get spanCount(): number {
		return this.finished.length;
	}

	// No droppedSpanCount getter: nothing read it but its own test. The counter
	// itself is live — it surfaces as `otel.dropped_spans_count` on the payload,
	// which is where the behaviour is asserted.

	/**
	 * Build the OTLP payload. Returns null when there is nothing to send, so
	 * callers can skip the POST entirely.
	 */
	toPayload(): OtlpTracePayload | null {
		if (this.finished.length === 0) return null;

		const resourceAttributes = attrs(
			this.droppedSpans > 0 ? { ...this.resource, 'otel.dropped_spans_count': this.droppedSpans } : this.resource,
		) as OtlpKeyValue[] | undefined;

		return {
			resourceSpans: [
				{
					resource: { attributes: resourceAttributes ?? [] },
					scopeSpans: [
						{
							scope: {
								name: this.scopeName,
								...(this.scopeVersion ? { version: this.scopeVersion } : {}),
								...(this.scopeAttributes ? { attributes: attrs(this.scopeAttributes) } : {}),
							},
							spans: this.finished.map((s) => s._toOtlp()),
						},
					],
				},
			],
		};
	}
}
